import { ReactNode, useState } from 'react'
import { IndexDBService } from '@eo-storefronts/eo-core'
import { useAsyncEffect } from '~/src/hooks/useAsyncEffect'
import { useSetEoState } from '~/src/hooks/useEoState'
import { CART_STATE } from '~/src/stores/cart'
import { MARKETPLACE_VIEWED_RECENTLY_STATE } from '~/src/stores/marketplace/viewed-recently'
import useSetAuthState from '~/src/hooks/auth/useSetAuthState'
import { CHECKOUT_FORM_STATE, CheckoutFormState } from '~/src/stores/checkout'
import { LAST_GUEST_ORDER_ID_STATE } from '~/src/stores/orders'
import ApiConfigUtils from '~/src/utils/ApiConfigUtils'

interface Props {
  children: ReactNode,
}

const IndexDbHydrate = ({ children }: Props) => {
  const [ hydrated, setHydrated ] = useState<boolean>(false)
  const setCartState = useSetEoState(CART_STATE)
  const setMarketplaceViewedRecentlyState = useSetEoState(MARKETPLACE_VIEWED_RECENTLY_STATE)
  const setCheckoutFormState = useSetEoState(CHECKOUT_FORM_STATE)
  const setLastGuestOrderId = useSetEoState(LAST_GUEST_ORDER_ID_STATE)
  const { setAuthState } = useSetAuthState()
  const indexDbService = new IndexDBService()
  const keys = [
    {
      indexDbKey: 'checkoutState',
      time: -1,
      setState: (checkoutForm: CheckoutFormState) => {
        setCheckoutFormState((state: CheckoutFormState) => ({
          ...state,
          delivery_method: {
            ...state.delivery_method,
            address: checkoutForm.delivery_method.address
          }
        }))
      }
    },
    {
      indexDbKey: 'marketplaceViewedRecentlyState',
      time: 172800000,
      setState: setMarketplaceViewedRecentlyState
    },
    {
      indexDbKey: 'cartState',
      time: 604800000,
      setState: setCartState
    },
    {
      indexDbKey: 'authenticationState',
      time: 657000000,
      setState: setAuthState
    },
    {
      indexDbKey: 'lastGuestOrderIdState',
      time: -1,
      setState: setLastGuestOrderId
    }
  ]

  useAsyncEffect(async () => {
    await ApiConfigUtils.init()

    for (const key of keys) {
      const [ setTime, indexDbValue ] = await Promise.all([
        indexDbService.get<number>(`${key.indexDbKey}-time`),
        indexDbService.get<any>(key.indexDbKey)
      ])

      if (indexDbValue && ((setTime && setTime + key.time > Date.now()) || key.time === -1)) {
        key.setState(indexDbValue)
      } else {
        void indexDbService.delMany([ `${key.indexDbKey}-time`, key.indexDbKey ])
      }
    }

    setHydrated(true)
  }, [])

  if (!hydrated) {
    return null
  }

  return <>{children}</>
}

export default IndexDbHydrate
